import { Sparkles, Check, TrendingUp } from "lucide-react";

const choices = [
  {
    label: "Agree now — it feels cheap monthly",
    sub: "$6/mo · barely noticed",
    state: "muted",
  },
  {
    label: "Check if it fits your budget first",
    sub: "Recommended · Lumi flagged this",
    state: "selected",
  },
  {
    label: "Use savings and cancel later",
    sub: "Cash flow risk",
    state: "muted",
  },
] as const;

const WhyHeirWiseSection = () => (
  <section className="relative py-16 sm:py-20 overflow-hidden">
    <div aria-hidden className="pointer-events-none absolute -left-32 top-1/3 h-[520px] w-[520px] rounded-full bg-[radial-gradient(circle,rgba(34,211,238,0.16),transparent_60%)] blur-3xl" />
    <div aria-hidden className="pointer-events-none absolute -right-32 bottom-1/4 h-[480px] w-[480px] rounded-full bg-[radial-gradient(circle,rgba(167,139,250,0.18),transparent_60%)] blur-3xl" />

    <div className="container mx-auto px-6 max-w-6xl relative">
      <div className="grid lg:grid-cols-[0.92fr_1.08fr] gap-12 lg:gap-16 items-center">
        {/* Copy */}
        <div>
          <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.3em] text-cyan-300/90 mb-4">
            <span className="h-1.5 w-1.5 rounded-full bg-cyan-400 shadow-[0_0_10px_rgba(34,211,238,0.95)]" />
            The HeirWise difference
          </div>
          <h2 className="font-heading text-3xl sm:text-4xl md:text-5xl text-white leading-[1.05]">
            Real decisions,{" "}
            <span className="bg-gradient-to-r from-cyan-200 via-indigo-200 to-violet-200 bg-clip-text text-transparent">
              before real consequences.
            </span>
          </h2>
          <p className="mt-5 text-white/70 leading-relaxed max-w-md">
            Teens build practical judgment by applying concepts, not only memorizing definitions. They practice choices involving spending, saving, borrowing, incentives, business, risk, and tradeoffs.
          </p>
          <p className="mt-4 text-white/55 leading-relaxed max-w-md text-sm">
            Lessons combine decision practice with feedback and assessment. The learner considers a choice, sees the consequence, and gets help understanding why.
          </p>

          <div className="mt-7 flex flex-wrap items-center gap-x-5 gap-y-2 text-[11px] text-white/55">
            {[
              ["bg-cyan-400", "Practical decision checks"],
              ["bg-indigo-400", "Adaptive feedback"],
              ["bg-violet-400", "Parent insight, quietly"],
            ].map(([dot, label]) => (
              <span key={label} className="inline-flex items-center gap-2">
                <span className={`h-1 w-1 rounded-full ${dot}`} />
                {label}
              </span>
            ))}
          </div>
        </div>

        {/* Decision Moment holo card */}
        <div className="relative">
          {/* Soft portal halo behind the card */}
          <div aria-hidden className="pointer-events-none absolute -inset-10 rounded-[40px] bg-[radial-gradient(circle_at_50%_50%,rgba(99,102,241,0.22),transparent_65%)] blur-2xl" />

          <div className="relative rounded-3xl p-[1px] bg-gradient-to-br from-cyan-400/45 via-indigo-500/35 to-violet-500/45 shadow-[0_30px_90px_-30px_rgba(99,102,241,0.7)]">
            <div className="relative rounded-3xl bg-[#0e0d24]/92 backdrop-blur-2xl p-6 sm:p-7 overflow-hidden">
              <div aria-hidden className="absolute -top-16 -right-16 h-48 w-48 rounded-full bg-[radial-gradient(circle,rgba(34,211,238,0.25),transparent_60%)] blur-2xl" />

              {/* Header */}
              <div className="relative flex items-center justify-between">
                <div className="inline-flex items-center gap-2 text-[9px] uppercase tracking-[0.3em] text-cyan-300/90">
                  <Sparkles className="h-3 w-3" />
                  Decision Check · Real-World Scenario
                </div>
                <div className="text-[9px] uppercase tracking-[0.22em] text-white/40">Lesson 03 · Cash Flow</div>
              </div>

              {/* Scenario */}
              <div className="relative mt-5 rounded-2xl border border-white/10 bg-white/[0.03] p-4">
                <div className="text-[10px] uppercase tracking-[0.28em] text-white/45 mb-1.5">Scenario</div>
                <div className="text-white text-base sm:text-[17px] leading-snug font-medium">
                  Your friend wants you to split a subscription you barely use. What do you do?
                </div>
              </div>

              {/* Choices */}
              <div className="relative mt-4 space-y-2.5">
                {choices.map((c) => {
                  const isSelected = c.state === "selected";
                  return (
                    <div
                      key={c.label}
                      className={`flex items-start gap-3 rounded-xl border px-4 py-3 transition-all ${
                        isSelected
                          ? "border-cyan-300/55 bg-cyan-400/10 shadow-[0_10px_40px_-12px_rgba(34,211,238,0.55)]"
                          : "border-white/8 bg-white/[0.02]"
                      }`}
                    >
                      <span
                        className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full ring-1 ${
                          isSelected ? "ring-cyan-300/80 bg-cyan-400/20" : "ring-white/15 bg-white/[0.04]"
                        }`}
                      >
                        {isSelected ? <Check className="h-3 w-3 text-cyan-200" /> : null}
                      </span>
                      <div className="min-w-0">
                        <div className={`text-sm font-medium leading-snug ${isSelected ? "text-white" : "text-white/80"}`}>
                          {c.label}
                        </div>
                        <div className={`mt-0.5 text-[11px] ${isSelected ? "text-cyan-200/85" : "text-white/45"}`}>
                          {c.sub}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Lumi hint */}
              <div className="relative mt-5 rounded-2xl border border-indigo-300/30 bg-indigo-500/[0.08] p-3.5">
                <div className="flex items-center gap-2 text-[9px] uppercase tracking-[0.28em] text-indigo-200/90">
                  <span className="relative flex h-1.5 w-1.5">
                    <span className="absolute inset-0 rounded-full bg-indigo-300 animate-ping opacity-75" />
                    <span className="relative h-1.5 w-1.5 rounded-full bg-indigo-300" />
                  </span>
                  Lumi · Hint
                </div>
                <p className="mt-1.5 text-[12.5px] text-white/85 leading-snug">
                  Before you choose, check what this does to your future cash flow.
                </p>
              </div>

              {/* Consequence meter */}
              <div className="relative mt-4 flex items-center gap-3">
                <div className="flex-1">
                  <div className="flex items-center justify-between text-[9px] uppercase tracking-[0.26em] text-white/45 mb-1">
                    <span>Consequence preview</span>
                    <span className="text-emerald-300/90 inline-flex items-center gap-1">
                      <TrendingUp className="h-3 w-3" /> Cash flow protected
                    </span>
                  </div>
                  <div className="h-1.5 rounded-full bg-white/5 overflow-hidden">
                    <div className="h-full w-[72%] rounded-full bg-gradient-to-r from-cyan-400 via-indigo-400 to-emerald-400" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
);

export default WhyHeirWiseSection;